import React from "react";
import { useLocation, Link, useParams } from "react-router-dom";
import { Row, Col } from "react-grid-system";
import {
  FaCheck,
  FaArrowLeft,
  FaApple,
} from "react-icons/fa";
import Slider from "react-slick";
import { getProjectById } from "../../constants/portfolioData";
import "./IPhoneFrame.css";

const ProjectDetail = () => {
  const { id } = useParams();
  const { state } = useLocation();

  let project = state?.project;
  
  if (!project && id) {
    project = getProjectById(id);
  }
  
  if (!project && state?.projectName) {
    project = {
      title: state.projectName,
      tagline: state.projectSubTitle,
      role: state.role,
      client: state.client,
      duration: state.duration,
      techStack: state.techStack,
      viewAppUrl: state.viewAppUrl,
      mockup: state.mockup,
      screenshots: state.screenshots || [],
      showcaseImage: state.showcaseImage,
      banner: state.projectBanner?.src || state.projectBanner,
      overview: state.overview || state.description,
      keyFeatures: state.keyFeatures || [],
      technicalApproach: state.technicalApproach || [],
      challengesSolved: state.challengesSolved || state.challenges?.map(c => c.text) || [],
      outcome: state.outcome || [],
      responsibilities: state.responsibilities || [],
      finalNote: state.finalNote,
      androidlink: state.androidlink,
      ioslink: state.ioslink,
    };
  }

  if (!project || (!project.title && !project.projectName)) {
    return (
      <section className="section section-project-detail">
        {/* <div className="display-spacing"> */}
          <div className="project-detail project-detail--error">
            <p>Project not found.</p>
            <Link to="/portfolio" className="button button-lg">
              Back to Portfolio
            </Link>
          </div>
        {/* </div> */}
      </section>
    );
  }

  const {
    title,
    tagline,
    role,
    client,
    duration,
    techStack,
    viewAppUrl,
    mockup,
    screenshots,
    showcaseImage,
    banner,
    overview,
    keyFeatures,
    technicalApproach,
    challengesSolved,
    outcome,
    responsibilities,
    finalNote,
    androidlink,
    ioslink,
    keyFacts, // legacy
  } = project;

  const appLinks = [
    { href: androidlink, icon: "/assets/images/storeImages/play-store.svg", alt: "Play Store" },
    { href: ioslink, icon: "/assets/images/storeImages/app-store.svg", alt: "App Store" },
  ].filter(link => link.href);

  // slider settings
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  // Legacy Mapping: if outcome is missing but keyFacts exist, use keyFacts
  const outcomeData = (outcome && outcome.length > 0) 
    ? outcome 
    : (keyFacts?.map(fact => ({ title: "Key Fact", description: fact, icon: "ti-check-box" })) || []);

  // Legacy Mapping: if challengesSolved is missing but challenges exist
  const challengesData = (challengesSolved && challengesSolved.length > 0)
    ? challengesSolved
    : (project.challenges?.map(c => c.text) || []);

  return (
    <>
      {/* Hero Section: Premium Background */}
      <section className="section section-project-detail-hero">
        <div className="project-detail">
          <Link to="/portfolio" className="project-detail__back-link">
            <FaArrowLeft /> Back to Portfolio
          </Link>

          {/* Header Section */}
          <Row className="project-detail__header" align="center">
            <Col md={12} lg={7}>
              <h1 className="project-detail__title">{title}</h1>
              {tagline && <h3 className="project-detail__tagline">{tagline}</h3>}
              
              <div className="project-detail__meta-inline">
                {[
                  { label: "Role", value: role },
                  { label: "Client", value: client },
                  { label: "Duration", value: duration },
                  { label: "Tech Stack", value: techStack },
                ]
                  .filter((item) => item.value)
                  .map((item, index, array) => (
                    <span key={item.label} className="meta-inline-item">
                      <strong>{item.label}:</strong> {item.value}
                      {index < array.length - 1 && <span className="meta-divider"> | </span>}
                    </span>
                  ))}
              </div>

              <div className="project-detail__actions" style={{ marginTop: "1.5rem" }}>
                 <div className="flex items-center gap-4">
                                      <div className="p-2 rounded-2xl bg-[#1A1D21] border border-white/20 shadow-xl group">
                                          <img src="/assets/images/intro/atom.webp" className="w-8 h-8 group-hover:scale-110 transition-transform" />
                                      </div>
                                      <div className="p-3 rounded-2xl bg-[#1A1D21] border border-white/20 shadow-xl group">
                                          <FaApple className="text-3xl text-white group-hover:scale-110 transition-transform" />
                                      </div>
                                      <div className="p-3 rounded-2xl bg-[#1A1D21] border border-white/20 shadow-xl group">
                                          <img src="/assets/images/intro/android1.webp" className="w-8 h-8 group-hover:scale-110 transition-transform" />
                                      </div>
                                  </div>
                {/* <div className="project-detail__app-buttons">
                  {appLinks.map((link, i) => (
                    <a key={i} href={link.href} target="_blank" rel="noopener noreferrer" className="project-detail__app-btn">
                      <img src={link.icon} alt={link.alt} />
                    </a>
                  ))} */}
                  {/* {viewAppUrl && appLinks.length === 0 && (
                    <a href={viewAppUrl} target="_blank" rel="noopener noreferrer" className="project-detail__btn project-detail__btn--primary">
                      View App
                    </a>
                  )} */}
                {/* </div> */}
              </div>
            </Col>
            <Col md={12} lg={5}>
              <div className="project-detail__mockup-wrap">
                <div className="iphone-frame-container">
                  <div className="iphone-screen">
                    {screenshots && screenshots.length > 0 ? (
                      <Slider {...sliderSettings}>
                        {screenshots.map((img, idx) => (
                          <div key={idx}>
                            <img src={img} alt={`${title} screenshot ${idx + 1}`} />
                          </div>
                        ))}
                      </Slider>
                    ) : (
                      <img src={mockup || banner} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* Content Section: Standard Dark Background */}
      <section className="section section-project-detail-content">
        <div className="project-detail">
          {/* Project Overview */}
          {overview && (
            <div className="project-detail__section">
              <h2 className="project-detail__heading">Project Overview</h2>
              <p className="project-detail__overview">{overview}</p>
            </div>
          )}

            {/* Challenges Solved & My Responsibilities */}
          <div className="project-detail__section">
            <Row>
              <Col md={12} lg={(responsibilities?.length > 0) ? 6 : 12}>
                <h2 className="project-detail__heading">Challenges Solved</h2>
                <ul className="project-detail__bullet-list">
                  {challengesData?.map((challenge, i) => (
                    <li key={i}>{challenge}</li>
                  ))}
                </ul>
              </Col>
              {responsibilities && responsibilities.length > 0 && (
                <Col md={12} lg={6}>
                  <h2 className="project-detail__heading">My Responsibility</h2>
                  <ul className="project-detail__check-list">
                    {responsibilities?.map((resp, i) => (
                      <li key={i}>
                        <FaCheck /> {resp}
                      </li>
                    ))}
                  </ul>
                </Col>
              )}
            </Row>
          </div>

          {/* App Showcase Section */}
          {showcaseImage && (
            <div className="project-detail__section">
              <h2 className="project-detail__heading">App Screens Showcase</h2>
              <div className="project-detail__showcase">
                <img 
                  src={showcaseImage} 
                  alt="App Screens Showcase" 
                  style={{ width: '100%', height: 'auto', borderRadius: '20px', boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }} 
                />
              </div>
            </div>
          )}

          {/* Key Features Delivered */}
          {keyFeatures && keyFeatures.length > 0 && (
            <div className="project-detail__section">
              <h2 className="project-detail__heading">Key Features Delivered</h2>
              <Row>
                {keyFeatures.map((feature, i) => (
                  <Col key={i} md={6} lg={4} className="mb-12 sm:mb-20">
                    <div className="feature-item">
                      <div className="feature-item__head">
                        <i className={feature.icon || "ti-layers"}></i>
                        <h4>{feature.title}</h4>
                      </div>
                      <ul className="feature-item__points">
                        {feature.points.map((point, pIdx) => (
                          <li key={pIdx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {/* Technical Approach */}
          {technicalApproach && technicalApproach.length > 0 && (
            <div className="project-detail__section">
              <h2 className="project-detail__heading">Technical Approach</h2>
              <Row>
                {technicalApproach.map((tech, i) => (
                  <Col key={i} md={12} lg={4} className="mb-12">
                    <div className="tech-approach-item">
                      <h4>{tech.title}</h4>
                      <p>{tech.description}</p>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}

        

          {/* Outcome & Impact */}
          {outcomeData && outcomeData.length > 0 && (
            <div className="project-detail__section">
              <h2 className="project-detail__heading">Outcome & Impact</h2>
              <Row>
                {outcomeData.map((item, i) => (
                  <Col key={i} md={6} lg={6} className="mb-12 sm:mb-20">
                    <div className="outcome-item">
                      <div className="outcome-item__icon">
                        <i className={item.icon || "ti-crown"}></i>
                      </div>
                      <div className="outcome-item__content">
                        <h4>{item.title}</h4>
                        <p>{item.description}</p>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {/* Final Note Quote */}
          {finalNote && (
            <div className="project-detail__quote" style={{ marginBottom: "3rem" }}>
              <p>"{finalNote}"</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default ProjectDetail;
